import React from "react";
import { Activity, AlertTriangle, Layers, RefreshCw } from "lucide-react";
import CTABand from "../components/CTABand";
import { MEDIA } from "../lib/constants";
import { useSEO } from "../lib/seo";

export default function OperationalRisk() {
  useSEO({
    title:
      "SNF Operational Stabilization Consulting · Missouri",
    description:
      "On-site operational diagnostic, risk stratification, leadership restructure, and sustainable cadence installation for Missouri skilled nursing facilities and long-term care operators under pressure.",
    path: "/operational-risk",
  });
  return (
    <div data-testid="operational-page" className="bg-ivory">
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe">
          <div className="overline mb-5">Practice Area · Operational Risk and Stabilization</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6 max-w-4xl">
            Stabilize the building <span className="italic text-teal">before stress becomes collapse.</span>
          </h1>
          <p className="font-sans text-lg text-navy/75 max-w-3xl leading-relaxed">
            Department heads running reactively. Communication fractured.
            Critical systems on paper but not in practice. AthenaCrest diagnoses
            the root pattern and installs the leadership cadence that holds.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src={MEDIA.corporateOffice}
              alt="Operational stabilization"
              loading="lazy"
              decoding="async"
              className="w-full aspect-[4/5] object-cover"
            />
          </div>
          <div className="lg:col-span-7 space-y-10">
            {[
              {
                icon: Activity,
                title: "Operational Diagnostic",
                body: "Three day on site assessment across nursing, social services, admissions, business office, and clinical leadership.",
              },
              {
                icon: AlertTriangle,
                title: "Risk Stratification",
                body: "Findings stratified by financial, regulatory, and reputational exposure, then translated into ownership level language.",
              },
              {
                icon: Layers,
                title: "Leadership Restructure",
                body: "Department head coaching, IDT facilitation training, and accountability cadence installed at every level.",
              },
              {
                icon: RefreshCw,
                title: "Sustainable Cadence",
                body: "Weekly, monthly, and quarterly rhythms that survive the engagement and become operational identity.",
              },
            ].map((b) => (
              <div key={b.title} className="flex gap-6 border-b border-champagne-200 pb-8">
                <div className="w-14 h-14 border border-gold flex items-center justify-center flex-shrink-0">
                  <b.icon className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-navy mb-2">{b.title}</h3>
                  <p className="font-sans text-base text-navy/70 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="operational-cta-band"
        eyebrow="Stabilize Now"
        headline="When three department heads have turned over in 90 days, it is already late."
        sub="The earlier AthenaCrest enters, the smaller the engagement and the larger the recovery."
      />
    </div>
  );
}
